import { CLUSTER_MAX_GAP_MS } from "./geo";
import type { Incident, IncidentStatus } from "./types";

const HOUR = 60 * 60 * 1000;

/**
 * Polar VIIRS/MODIS often leave 6–12 h between overpasses of the same place.
 * Short map windows still keep a fire if the next pass has not happened yet.
 */
export function incidentLookbackMs(windowMs: number): number {
  return Math.max(windowMs, CLUSTER_MAX_GAP_MS);
}

/**
 * Status is derived from timestamps + FRP change, not from a model.
 * Rapid intensification: large relative AND absolute rise over ~40 minutes of observations.
 */
export function classifyIncident(
  incident: Pick<Incident, "first_seen" | "last_seen" | "frp_mw" | "frp_prev_mw">,
  nowMs: number,
): IncidentStatus {
  const first = Date.parse(incident.first_seen);
  const last = Date.parse(incident.last_seen);
  const age = nowMs - last;
  const life = last - first;
  const prev = incident.frp_prev_mw;
  const delta = prev == null ? 0 : incident.frp_mw - prev;
  const pct = prev && prev > 0 ? (delta / prev) * 100 : 0;

  // Silence through one polar cycle is "waiting", not "out".
  if (age > CLUSTER_MAX_GAP_MS) return "cooling";
  if (delta >= 15 && pct >= 50) return "rapidly_intensifying";
  if (delta >= 5 && pct >= 15) return "growing";
  if (delta <= -8 || pct <= -25) return "cooling";
  if (life < 1 * HOUR && age < 1 * HOUR) return "newly_detected";
  if (age < 3 * HOUR) return "holding";
  return "active";
}

/** Last-overpass FRP high enough that this is a campaign fire, not a lone ag burn. */
export const IMPORTANT_FRP_MW = 50;
/** At least a small cluster — one 50 MW pixel is not an "event". */
export const IMPORTANT_MIN_DETECTIONS = 3;
/** Keep a campaign clickable under Important after the heatmap dies down. */
export const IMPORTANT_HOLD_MS = 48 * HOUR;

/** Fields the Major-events hold reads. Live API rows often lack thermal_last_seen. */
export type ImportantFields = Pick<Incident, "frp_mw" | "status" | "detection_count"> & {
  last_seen?: string;
  thermal_last_seen?: string;
  last_important_at?: string | null;
  sources?: string[];
};

/** True while this overpass still looks like a campaign (size, not trend). */
export function qualifiesAsImportantNow(
  incident: Pick<Incident, "frp_mw" | "detection_count">,
): boolean {
  return incident.frp_mw >= IMPORTANT_FRP_MW && incident.detection_count >= IMPORTANT_MIN_DETECTIONS;
}

/** Latest FRP-bearing look. S3 last_seen is not this — it has no megawatts. */
function lastFrpLookMs(incident: ImportantFields): number {
  if (incident.thermal_last_seen) return Date.parse(incident.thermal_last_seen);
  if ((incident.sources ?? []).includes("S3")) return NaN;
  return incident.last_seen ? Date.parse(incident.last_seen) : NaN;
}

/**
 * Last reporting the leftover hold may extend from.
 * Prefer thermal_last_seen; otherwise last_seen (API rows do not store the former).
 */
function lastHoldLookMs(incident: ImportantFields): number {
  if (incident.thermal_last_seen) return Date.parse(incident.thermal_last_seen);
  return incident.last_seen ? Date.parse(incident.last_seen) : NaN;
}

/**
 * Origin of the 48 h hold once FRP has dropped.
 * Leftover looks during the stamp window extend it to those looks — they do not
 * move last_important_at. Looks after that window do not restart it (furnaces).
 */
export function importantHoldFromMs(incident: ImportantFields): number {
  const stamped = incident.last_important_at ? Date.parse(incident.last_important_at) : NaN;
  if (!Number.isFinite(stamped)) return NaN;
  const last = lastHoldLookMs(incident);
  if (Number.isFinite(last) && last >= stamped && last - stamped <= IMPORTANT_HOLD_MS) return last;
  return stamped;
}

/**
 * Important = campaign-sized now, or still within 48 h of last looking that way
 * (leftover thermal looks in that window extend the listing to 48 h after them).
 * Cooling / a quiet polar gap does not drop a High Fens-scale fire off the list.
 */
export function isImportantEvent(incident: ImportantFields, nowMs = Date.now()): boolean {
  if (incident.detection_count < IMPORTANT_MIN_DETECTIONS) return false;
  // Live and still large — rising FRP is not required.
  if (incident.status !== "cooling" && incident.frp_mw >= IMPORTANT_FRP_MW) return true;
  // Last FRP overpass was still campaign-sized. An S3 last_seen must not
  // restart this clock — that look did not contribute the megawatts.
  const lastFrp = lastFrpLookMs(incident);
  if (Number.isFinite(lastFrp) && nowMs - lastFrp <= IMPORTANT_HOLD_MS && incident.frp_mw >= IMPORTANT_FRP_MW) {
    return true;
  }
  const from = importantHoldFromMs(incident);
  return Number.isFinite(from) && nowMs - from <= IMPORTANT_HOLD_MS;
}

/** During playback, hide fires that have not started yet. Otherwise keep them clickable for the whole window. */
export function incidentVisibleAtPlayhead(firstSeen: string, playheadMs: number): boolean {
  return Date.parse(firstSeen) <= playheadMs;
}

export function frpDeltaPct(current: number, prev: number | null): number | null {
  if (prev == null || prev <= 0) return null;
  return ((current - prev) / prev) * 100;
}

export function incidentLabel(country: string, firstSeen: string, seed: string): string {
  const year = firstSeen.slice(0, 4);
  // Five digits from the seed so the public id is stable if we re-ingest the same first pixel.
  let n = 0;
  for (let i = 0; i < seed.length; i++) n = (n * 33 + seed.charCodeAt(i)) >>> 0;
  const serial = String(n % 100000).padStart(5, "0");
  return `${country}-${year}-${serial}`;
}
